import React, { useState, useMemo } from "react";
import { useTranslation } from "../i18n";
import { m, AnimatePresence } from "framer-motion";
import { Coins, CheckCircle, Zap, Users, ShieldCheck } from "lucide-react";
import { clsx } from "clsx";
import "./TokensSection.css";
import { signupUrl, useSignupUrl } from "../utils/signup";

const DEFAULT_PAGES = 500;
const LOCALE = { es: "es-ES", en: "en-GB", it: "it-IT", pt: "pt-PT", de: "de-DE", fr: "fr-FR" };

// Plan Configuration
const plans = [
  {
    id: "free",
    name: {
      es: "Plan Gratuito",
      en: "Free Plan", de: "Kostenloser Tarif", fr: "Offre gratuite",
      it: "Piano Gratuito",
      pt: "Plano Gratuito",
    },
    monthlyPrice: 0,
    yearlyPrice: 0,
    credits: 20,
    minPages: 0,
    maxPages: 20,
    features: {
      credits: {
        // 20 páginas AL MES desde el 24-9-2026 (antes, 20 una sola vez).
        es: "20 páginas al mes, gratis para siempre",
        en: "20 pages a month, free forever", de: "20 Seiten pro Monat, dauerhaft kostenlos", fr: "20 pages par mois, gratuit pour toujours",
        it: "20 pagine al mese, gratis per sempre",
        pt: "20 páginas por mês, grátis para sempre",
      },
      support: {
        es: "Soporte básico",
        en: "Basic support", de: "Basis-Support", fr: "Support de base",
        it: "Supporto base",
        pt: "Suporte básico",
      },
      scanners: {
        es: "1 Plantilla",
        en: "1 Template", de: "1 Vorlage", fr: "1 modèle",
        it: "1 Modello",
        pt: "1 Modelo",
      },
      members: {
        es: "1 Miembro de equipo",
        en: "1 Team member", de: "1 Teammitglied", fr: "1 membre d'équipe",
        it: "1 Membro del team",
        pt: "1 Membros da equipe",
      },
      api: {
        es: "Sin acceso API",
        en: "No API access", de: "Kein API-Zugang", fr: "Pas d'accès API",
        it: "Nessun accesso API",
        pt: "Sem acesso API",
      },
      unlimited_scanners: false,
      api_access: false,
    },
    color: "bg-gray-100",
    textColor: "text-gray-600",
    buttonColor: "bg-gray-800 hover:bg-gray-900",
  },
  {
    id: "basic",
    name: { es: "Basic", en: "Basic", it: "Basic", pt: "Basic", de: "Basic", fr: "Basic" },
    monthlyPrice: 15,
    yearlyPrice: 150,
    credits: 200,
    minPages: 21,
    maxPages: 200,
    features: {
      credits: {
        es: "200 créditos / mes",
        en: "200 credits / month", de: "200 Credits / Monat", fr: "200 crédits / mois",
        it: "200 crediti / mese",
        pt: "200 créditos / mês",
      },
      support: {
        es: "Soporte prioritario",
        en: "Priority support", de: "Priorisierter Support", fr: "Support prioritaire",
        it: "Supporto prioritario",
        pt: "Suporte prioritário",
      },
      scanners: {
        es: "Plantillas ilimitadas",
        en: "Unlimited templates", de: "Unbegrenzte Vorlagen", fr: "Modèles illimités",
        it: "Modelli illimitati",
        pt: "Modelos ilimitados",
      },
      members: {
        es: "2 Miembros de equipo",
        en: "2 Team members", de: "2 Teammitglieder", fr: "2 membres d'équipe",
        it: "2 Membri del team",
        pt: "2 Membros da equipe",
      },
      api: {
        es: "Acceso API profesional incluido",
        en: "Professional API access included", de: "Professioneller API-Zugang inklusive", fr: "Accès API professionnel inclus",
        it: "Accesso API professionale incluso",
        pt: "Acesso API profissional incluído",
      },
      unlimited_scanners: true,
      api_access: true,
    },
    color: "bg-blue-50",
    textColor: "text-blue-600",
    buttonColor: "bg-blue-600 hover:bg-blue-700",
  },
  {
    id: "standard",
    name: { es: "Standard", en: "Standard", it: "Standard", pt: "Standard", de: "Standard", fr: "Standard" },
    monthlyPrice: 45,
    yearlyPrice: 450,
    credits: 1000,
    minPages: 201,
    maxPages: 1000,
    features: {
      credits: {
        es: "1,000 créditos / mes",
        en: "1,000 credits / month", de: "1.000 Credits / Monat", fr: "1 000 crédits / mois",
        it: "1,000 crediti / mese",
        pt: "1,000 créditos / mês",
      },
      support: {
        es: "Soporte prioritario",
        en: "Priority support", de: "Priorisierter Support", fr: "Support prioritaire",
        it: "Supporto prioritario",
        pt: "Suporte prioritário",
      },
      scanners: {
        es: "Plantillas ilimitadas",
        en: "Unlimited templates", de: "Unbegrenzte Vorlagen", fr: "Modèles illimités",
        it: "Modelli illimitati",
        pt: "Modelos ilimitados",
      },
      members: {
        es: "5 Miembros de equipo",
        en: "5 Team members", de: "5 Teammitglieder", fr: "5 membres d'équipe",
        it: "5 Membri del team",
        pt: "5 Membros da equipe",
      },
      api: {
        es: "Acceso API profesional incluido",
        en: "Professional API access included", de: "Professioneller API-Zugang inklusive", fr: "Accès API professionnel inclus",
        it: "Accesso API professionale incluso",
        pt: "Acesso API profissional incluído",
      },
      unlimited_scanners: true,
      api_access: true,
    },
    recommended: true,
    color: "bg-primary-50",
    textColor: "text-primary-600",
    buttonColor: "bg-primary-600 hover:bg-primary-700",
  },
  {
    id: "business",
    name: { es: "Business", en: "Business", it: "Business", pt: "Business", de: "Business", fr: "Business" },
    monthlyPrice: 95,
    yearlyPrice: 950,
    credits: 3500,
    minPages: 1001,
    maxPages: 3500,
    features: {
      credits: {
        es: "3,500 créditos / mes",
        en: "3,500 credits / month", de: "3.500 Credits / Monat", fr: "3 500 crédits / mois",
        it: "3,500 crediti / mese",
        pt: "3,500 créditos / mês",
      },
      support: {
        es: "Soporte Premium",
        en: "Premium support", de: "Premium-Support", fr: "Support premium",
        it: "Supporto Premium",
        pt: "Suporte Premium",
      },
      scanners: {
        es: "Plantillas ilimitadas",
        en: "Unlimited templates", de: "Unbegrenzte Vorlagen", fr: "Modèles illimités",
        it: "Modelli illimitati",
        pt: "Modelos ilimitados",
      },
      members: {
        es: "20 Miembros de equipo",
        en: "20 Team members", de: "20 Teammitglieder", fr: "20 membres d'équipe",
        it: "20 Membri del team",
        pt: "20 Membros da equipe",
      },
      api: {
        es: "Acceso API profesional incluido",
        en: "Professional API access included", de: "Professioneller API-Zugang inklusive", fr: "Accès API professionnel inclus",
        it: "Accesso API professionale incluso",
        pt: "Acesso API profissional incluído",
      },
      unlimited_scanners: true,
      api_access: true,
    },
    color: "bg-indigo-50",
    textColor: "text-indigo-600",
    buttonColor: "bg-indigo-600 hover:bg-indigo-700",
  },
  {
    id: "enterprise",
    name: {
      es: "Enterprise",
      en: "Enterprise",
      it: "Enterprise",
      pt: "Enterprise",
    },
    monthlyPrice: 195,
    yearlyPrice: 1950,
    credits: 15000,
    minPages: 3501,
    maxPages: 15000,
    features: {
      credits: {
        es: "15,000 créditos / mes",
        en: "15,000 credits / month", de: "15.000 Credits / Monat", fr: "15 000 crédits / mois",
        it: "15,000 crediti / mese",
        pt: "15,000 créditos / mês",
      },
      support: {
        es: "Soporte Dedicado",
        en: "Dedicated support", de: "Persönlicher Support", fr: "Support dédié",
        it: "Supporto Dedicato",
        pt: "Suporte Dedicado",
      },
      scanners: {
        es: "Plantillas ilimitadas",
        en: "Unlimited templates", de: "Unbegrenzte Vorlagen", fr: "Modèles illimités",
        it: "Modelli illimitati",
        pt: "Modelos ilimitados",
      },
      members: {
        es: "Miembros ilimitados",
        en: "Unlimited members", de: "Unbegrenzte Mitglieder", fr: "Membres illimités",
        it: "Membri illimitati",
        pt: "Membros ilimitados",
      },
      api: {
        es: "Acceso API profesional incluido",
        en: "Professional API access included", de: "Professioneller API-Zugang inklusive", fr: "Accès API professionnel inclus",
        it: "Accesso API professionale incluso",
        pt: "Acesso API profissional incluído",
      },
      unlimited_scanners: true,
      api_access: true,
    },
    color: "bg-purple-50",
    textColor: "text-purple-600",
    buttonColor: "bg-purple-600 hover:bg-purple-700",
  },
];

// Función no lineal para mapear porcentaje del slider a créditos
const mapSliderToCredits = (percentage) => {
  // Rangos no lineales para dar más resolución a planes baratos
  // 0-15%: 0-200 créditos (free, basic)
  // 15-40%: 200-1000 créditos (standard)
  // 40-70%: 1000-3500 créditos (business)
  // 70-100%: 3500-15000 créditos (enterprise)

  if (percentage <= 0.15) {
    return Math.round((percentage / 0.15) * 200);
  } else if (percentage <= 0.4) {
    return Math.round(200 + ((percentage - 0.15) / 0.25) * 800);
  } else if (percentage <= 0.7) {
    return Math.round(1000 + ((percentage - 0.4) / 0.3) * 2500);
  } else {
    return Math.round(3500 + ((percentage - 0.7) / 0.3) * 11500);
  }
};

// Función inversa para mapear créditos a porcentaje del slider
const mapCreditsToSlider = (credits) => {
  if (credits <= 200) {
    return (credits / 200) * 0.15;
  } else if (credits <= 1000) {
    return 0.15 + ((credits - 200) / 800) * 0.25;
  } else if (credits <= 3500) {
    return 0.4 + ((credits - 1000) / 2500) * 0.3;
  } else {
    return 0.7 + ((credits - 3500) / 11500) * 0.3;
  }
};

export default function TokensSection() {
  const { t, lang } = useTranslation();
  const signupHref = useSignupUrl(lang);
  // Valor inicial: 500 páginas/mes. Antes era 196, un número arbitrario que caía
  // en Basic, así que la etiqueta «Recomendado» (que lleva Standard) no se veía
  // nunca salvo que el visitante moviera el slider. 500 es una gestoría pequeña
  // (≈25 clientes × 20 facturas) — el cliente al que apunta la web — y cae en
  // Standard con la etiqueta a la vista.
  const [sliderValue, setSliderValue] = useState(mapCreditsToSlider(DEFAULT_PAGES)); // porcentaje 0-1
  const [frequency, setFrequency] = useState("yearly"); // monthly | yearly

  // Calcular páginas/creditos a partir del slider no lineal
  const pagesPerMonth = useMemo(
    () => mapSliderToCredits(sliderValue),
    [sliderValue],
  );

  const activePlan = useMemo(() => {
    if (pagesPerMonth <= 20) return plans[0];
    if (pagesPerMonth <= 200) return plans[1];
    if (pagesPerMonth <= 1000) return plans[2];
    if (pagesPerMonth <= 3500) return plans[3];
    return plans[4];
  }, [pagesPerMonth]);

  const language = lang || "es";
  const locale = LOCALE[language] || LOCALE.en;
  const nf = (n) => n.toLocaleString(locale);
  // Con decimales solo si los hay: 150 €/12 son 12,50 €, no «13 €» (que ×12 no
  // da los 150 € que se anuncian debajo).
  const eur = (n) =>
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(n);

  const price =
    frequency === "monthly" ? activePlan.monthlyPrice : activePlan.yearlyPrice / 12;

  // Ahorro anual real del plan que se está viendo. Sustituye al «Ahorra 20%»
  // fijo, que no era cierto en ningún plan: Basic, Business y Enterprise ahorran
  // un 16,7 %. Standard anunciaba 38 €/mes (1,3 %), pero Stripe cobra 45 €: ya
  // corregido, así que ahora los cuatro planes ahorran lo mismo. Por debajo
  // del 5 % no se anuncia.
  const yearlySavingPct =
    activePlan.monthlyPrice > 0
      ? Math.round((1 - activePlan.yearlyPrice / (activePlan.monthlyPrice * 12)) * 100)
      : 0;
  const pricePerPage = activePlan.credits > 0 ? price / activePlan.credits : 0;

  return (
    <section
      className="py-24 relative overflow-hidden bg-[#fafafa]"
      id="pricing"
    >
      {/* Background Decor */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-gradient-to-b from-primary-50/50 to-transparent rounded-[100%] blur-3xl opacity-60" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <m.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6"
          >
            {t("pricing.title") || "Simple, Transparent Pricing"}
          </m.h2>
          <m.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600"
          >
            {t("pricing.subtitle") ||
              "Pay only for what you scan. 1 Page = 1 Credit."}
          </m.p>
        </div>

        {/* Main Interface */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left: Interactive Calculator */}
          <div className="lg:col-span-7 space-y-8">
            <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-xl shadow-gray-100/50 relative overflow-hidden">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600">
                    <Coins className="w-5 h-5" />
                  </div>
                  <span className="font-semibold text-gray-900">
                    {t("pricing.calculate_investment")}
                  </span>
                </div>

                {/* Frequency Toggle — el indicador va DENTRO del botón activo
                    (inset-0) para que se ajuste a su ancho real; antes tenía un
                    ancho fijo del 50% y "Yearly + Save 20%" se salía del círculo. */}
                <div className="inline-flex items-center bg-gray-100 p-1 rounded-full">
                  {["monthly", "yearly"].map((freq) => {
                    const active = frequency === freq;
                    return (
                      <button
                        key={freq}
                        onClick={() => setFrequency(freq)}
                        className={clsx(
                          "relative px-4 py-1.5 text-xs font-bold rounded-full transition-colors whitespace-nowrap",
                          active ? "bg-white text-gray-900 shadow-sm" : "text-gray-500",
                        )}
                      >
                        <span className="relative z-10 inline-flex items-center">
                          {t(`pricing.${freq}`)}
                          {freq === "yearly" && yearlySavingPct >= 5 && (
                            <span className="text-[10px] text-green-600 font-extrabold ml-1">
                              {t("pricing.save_pct", { pct: yearlySavingPct })}
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Slider UI */}
              <div className="mb-12 px-2">
                <div className="flex justify-between text-sm font-medium text-gray-400 mb-4">
                  <span>{t("pricing.pages_0")}</span>
                  <span>{t("pricing.pages_max")}</span>
                </div>

                <div className="relative h-12 flex items-center">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.001"
                    value={sliderValue}
                    onChange={(e) => setSliderValue(Number(e.target.value))}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-30"
                  />
                  {/* Track */}
                  <div className="w-full h-4 bg-gray-100 rounded-full overflow-hidden relative">
                    <m.div
                      className="absolute inset-y-0 left-0 bg-primary-500"
                      style={{ width: `${sliderValue * 100}%` }}
                    />
                  </div>
                  {/* Thumb */}
                  <m.div
                    className="absolute w-8 h-8 bg-white border-2 border-primary-500 rounded-full shadow-lg z-20 pointer-events-none flex items-center justify-center"
                    style={{
                      left: `calc(${sliderValue * 100}% - 16px)`,
                    }}
                  >
                    <div className="w-2 h-2 rounded-full bg-primary-500" />
                  </m.div>
                </div>
              </div>

              {/* Dynamic Feedback */}
              <div className="text-center">
                <p className="text-gray-500 mb-2">
                  {t("pricing.expect_to_process")}
                </p>
                <div className="text-5xl font-extrabold text-gray-900 mb-2 tabular-nums">
                  {nf(pagesPerMonth)}
                  <span className="text-lg font-semibold text-gray-400 ml-2">{t("pricing.pages_per_month")}</span>
                </div>
                {/* Antes repetía la misma cifra («196» y «196 créditos»). Ahora
                    da un dato que no está en ningún otro sitio: cuánto cuesta
                    cada página en el plan que corresponde. */}
                {pricePerPage > 0 && (
                  <p className="text-primary-600 font-semibold bg-primary-50 inline-block px-3 py-1 rounded-lg tabular-nums">
                    {t("pricing.price_per_page", {
                      price: new Intl.NumberFormat(locale, {
                        style: "currency",
                        currency: "EUR",
                        minimumFractionDigits: 3,
                        maximumFractionDigits: 3,
                      }).format(pricePerPage),
                    })}
                  </p>
                )}
                {pagesPerMonth > 0 && (
                  <m.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 text-green-700 font-medium flex items-center justify-center gap-2"
                  >
                    <span className="text-xl">✨</span>
                    {t("pricing.time_saving", {
                      hours: Math.round((pagesPerMonth * 3) / 60), // Assuming 3 mins per page manual work
                    })}
                  </m.p>
                )}
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-2 gap-4">
              {[
                {
                  icon: ShieldCheck,
                  title: t("pricing.features_secure"),
                  desc: t("pricing.features_secure_desc"),
                },
                {
                  icon: Zap,
                  title: t("pricing.features_fast"),
                  desc: t("pricing.features_fast_desc"),
                },
                {
                  icon: Users,
                  title: t("pricing.features_teams"),
                  desc: t("pricing.features_teams_desc"),
                },
                {
                  icon: CheckCircle,
                  title: t("pricing.features_scanners"),
                  desc: t("pricing.features_scanners_desc"),
                },
              ].map((f, i) => (
                <div
                  key={i}
                  className="bg-white/50 border border-gray-100 p-4 rounded-xl flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center text-gray-600">
                    <f.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm">
                      {f.title}
                    </h4>
                    <p className="text-xs text-gray-500">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Plan Card */}
          <div className="lg:col-span-5 relative">
            <AnimatePresence mode="wait">
              <m.div
                key={activePlan.id}
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className={clsx(
                  "rounded-3xl p-8 border-2 relative overflow-hidden",
                  activePlan.color,
                  activePlan.recommended
                    ? "border-primary-500 shadow-2xl shadow-primary-500/20"
                    : "border-transparent",
                )}
              >
                {activePlan.recommended && (
                  <div className="absolute top-0 right-0 bg-primary-500 text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl uppercase tracking-wider">
                    {t("pricing.recommended")}
                  </div>
                )}

                <div className="mb-6">
                  <h3
                    className={clsx(
                      "text-lg font-bold mb-1",
                      activePlan.textColor,
                    )}
                  >
                    {(activePlan.name[language] || activePlan.name.en)}
                  </h3>
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-extrabold text-gray-900 tracking-tight tabular-nums">
                      {eur(price)}
                    </span>
                    <span className="text-gray-500 font-medium">
                      {t("pricing.per_month")}
                    </span>
                  </div>
                  {frequency === "yearly" && (
                    <div className="text-xs text-green-600 font-bold mt-1">
                      {t("pricing.billed_yearly")} ({eur(activePlan.yearlyPrice)})
                    </div>
                  )}
                </div>

                <ul className="space-y-4 mb-8">
                  <li className="flex items-center gap-3">
                    <CheckCircle
                      className={clsx("w-5 h-5", activePlan.textColor)}
                    />
                    <span className="font-medium text-gray-900">
                      {activePlan.id === "free"
                        ? (activePlan.features.credits[language] || activePlan.features.credits.en)
                        : t("pricing.credits_per_month", { n: nf(activePlan.credits) })}
                    </span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle
                      className={clsx("w-5 h-5", activePlan.textColor)}
                    />
                    <span className="text-gray-700">
                      1 {t("pricing.page")} = 1 {t("pricing.credit")}
                    </span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle
                      className={clsx("w-5 h-5", activePlan.textColor)}
                    />
                    <span className="text-gray-700">
                      {(activePlan.features.support[language] || activePlan.features.support.en)}
                    </span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle
                      className={clsx("w-5 h-5", activePlan.textColor)}
                    />
                    <span className="text-gray-700">
                      {(activePlan.features.scanners[language] || activePlan.features.scanners.en)}
                    </span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle
                      className={clsx("w-5 h-5", activePlan.textColor)}
                    />
                    <span className="text-gray-700">
                      {(activePlan.features.members[language] || activePlan.features.members.en)}
                    </span>
                  </li>
                  {activePlan.id !== "free" && (
                    <li className="flex items-center gap-3">
                      <Zap className="w-5 h-5 text-amber-500 fill-amber-500" />
                      <span className="font-bold text-gray-900 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-100 flex items-center gap-1.5">
                        {(activePlan.features.api[language] || activePlan.features.api.en)}
                        <span className="bg-amber-500 text-white text-[8px] px-1 py-0.5 rounded uppercase font-black animate-pulse">
                          {{ es: "NUEVO", de: "NEU", fr: "NOUVEAU", it: "NUOVO", pt: "NOVO" }[language] || "NEW"}
                        </span>
                      </span>
                    </li>
                  )}
                </ul>

                {/* Era un <button> sin onClick ni enlace: el CTA principal de
                    la sección de precios no hacía nada. */}
                <a
                  href={signupHref}
                  target="_blank"
                  rel="noreferrer noopener"
                  onClick={(e) => {
                    e.currentTarget.href = signupUrl(lang);
                    if (window.gtag)
                      window.gtag("event", "signup_click", {
                        method: "pricing",
                        plan: activePlan.id,
                        frequency,
                        pages: pagesPerMonth,
                      });
                    if (window.fbq) window.fbq("track", "Lead", { content_name: `signup_pricing_${activePlan.id}` });
                  }}
                  className={clsx(
                    "block text-center w-full py-4 rounded-xl font-bold text-white shadow-lg transition-all transform hover:scale-[1.02] active:scale-[0.98]",
                    activePlan.buttonColor,
                  )}
                >
                  {t("pricing.start_free")}
                </a>
                <p className="text-center text-xs text-gray-500 mt-4">
                  {t("pricing.free_trial_hint")}
                </p>
              </m.div>
            </AnimatePresence>

            {/* Decor Behind */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-br from-gray-200 to-transparent blur-3xl -z-10 opacity-50 rounded-full" />
          </div>
        </div>
      </div>
    </section>
  );
}
